import React, { useEffect, useState } from 'react'
import defaultClasses from './ookaTradition.module.scss'
import { string, array } from 'prop-types'
import Link from 'next/link'
import resolveLinkProps from '@corratech/pylot-rich-content/src/PageBuilder/resolveLinkProps'
import cn from 'classnames'
import Slider from 'react-slick'
import { useTranslation } from 'next-i18next'
import { getFastlyUrl } from '@corratech/pylot-image'
import { useIsArabic } from '@components/common/IsArabic/useIsArabic'

const traditionMobileSliderSettings = {
    arrows: false,
    speed: 400,
    slidesToScroll: 1,
    slidesToShow: 1,
    draggable: true,
    infinite: true,
    dots: true,
    fade: false,
    swipe: true,
    touchMove: true
}

const OokaTradition = ({
    subheading,
    heading,
    link,
    buttonText,
    itemConfig
}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const { t } = useTranslation('common')
    const isArabic = useIsArabic()

    useEffect(() => {
        const initialVideoElement = document.querySelector('.trigger-video-one')
        if (initialVideoElement) {
            initialVideoElement.classList.add(defaultClasses['active'])
        }
    }, [])

    const sharedButtonClass = cn(
        defaultClasses['tradition-section-button'],
        defaultClasses['btn-primary'],
        'btn-primary'
    )

    let buttonLink
    if (typeof link === 'string') {
        const linkProps = resolveLinkProps(link)
        buttonLink = linkProps.to ? (
            <Link {...linkProps} passHref>
                <a
                    className={cn(
                        defaultClasses['tradition-section-link'],
                        sharedButtonClass
                    )}
                >
                    {buttonText}
                </a>
            </Link>
        ) : (
            <a
                className={cn(
                    defaultClasses['tradition-section-link'],
                    sharedButtonClass
                )}
                {...linkProps}
            >
                {buttonText}
            </a>
        )
    }

    return (
        <div className={defaultClasses['tradition-section']}>
            <span className={defaultClasses['tradition-section-label']}>
                {subheading}
            </span>
            <h2 className={defaultClasses['tradition-section-title']}>
                {heading}
            </h2>

            <div className={defaultClasses['tradition-section-videos-desktop']}>
                <div
                    className={
                        defaultClasses['tradition-section-videos-desktop-list']
                    }
                >
                    <div
                        style={{ padding: '100% 0 0 0', position: 'relative' }}
                    >
                        {itemConfig[activeIndex]?.image ? (
                            <img
                                src={getFastlyUrl({
                                    src: `${itemConfig[activeIndex].image}?${itemConfig[activeIndex].imageFastlyParam}`,
                                    quality:
                                        itemConfig[activeIndex].imageQuality
                                })}
                                alt={itemConfig[activeIndex].heading}
                                className={defaultClasses['desktop-image']}
                                loading="lazy"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    zIndex: 1,
                                    border: 'none',
                                    backgroundColor: 'transparent'
                                }}
                            />
                        ) : (
                            <iframe
                                id="ActiveVideo"
                                title={t('Tradition video')}
                                src={
                                    itemConfig[activeIndex]?.vimeoLink
                                        ? `${itemConfig[activeIndex]?.vimeoLink}?dnt=1&autopause=0&transparent=1&autoplay=1&loop=1&muted=1&badge=0&byline=0&portrait=0&title=0&background=1&quality=best`
                                        : ''
                                }
                                allow="autoplay; fullscreen"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                    border: 'none',
                                    backgroundColor: 'transparent'
                                }}
                            />
                        )}
                    </div>
                </div>

                <div
                    className={
                        defaultClasses['tradition-section-videos-desktop-text']
                    }
                >
                    {itemConfig.map((item, index) => (
                        <div
                            key={index}
                            onClick={() => setActiveIndex(index)}
                            className={cn(
                                defaultClasses[
                                    'tradition-section-videos-desktop-text-inner'
                                ],
                                {
                                    [defaultClasses['active']]:
                                        index === activeIndex
                                }
                            )}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && setActiveIndex(index)
                            }
                        >
                            <h5>{item.heading}</h5>
                            <p>{item.content}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className={defaultClasses['tradition-section-videos-mobile']}>
                <Slider
                    {...{ ...traditionMobileSliderSettings, rtl: isArabic }}
                >
                    {itemConfig.map((item, index) => (
                        <div
                            key={index}
                            className={defaultClasses['mobile-slider-item']}
                        >
                            <div
                                className={
                                    defaultClasses[
                                        'tradition-section-mobile-slide'
                                    ]
                                }
                            >
                                <div
                                    style={{
                                        padding: '100% 0 0 0',
                                        position: 'relative'
                                    }}
                                    className={
                                        defaultClasses[
                                            'tradition-section-mobile-img'
                                        ]
                                    }
                                >
                                    {item.image ? (
                                        <img
                                            src={`${item.image}?${item.imageFastlyParam}`}
                                            alt={item.heading}
                                            className={
                                                defaultClasses['mobile-image']
                                            }
                                            loading="lazy"
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                zIndex: 1,
                                                border: 'none',
                                                backgroundColor: 'transparent'
                                            }}
                                        />
                                    ) : (
                                        <iframe
                                            src={
                                                item.vimeoLinkMobile
                                                    ? `${item.vimeoLinkMobile}?dnt=1&autopause=0&transparent=0&autoplay=1&loop=1&muted=1&badge=0&byline=0&portrait=0&title=0&background=1&quality=best`
                                                    : item.vimeoLink
                                                    ? `${item.vimeoLink}?dnt=1&autopause=0&transparent=0&autoplay=1&loop=1&muted=1&badge=0&byline=0&portrait=0&title=0&background=1&quality=best`
                                                    : ''
                                            }
                                            style={{
                                                position: 'absolute',
                                                top: '0',
                                                left: '0',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                            title={`${item.heading}`}
                                            allow="autoplay; fullscreen; picture-in-picture"
                                            allowFullScreen
                                        />
                                    )}
                                </div>
                                <h5>{item.heading}</h5>
                                <p>{item.content}</p>
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>
            <div className={defaultClasses['tradition-section-button-wrapper']}>
                {buttonLink}
            </div>
        </div>
    )
}

OokaTradition.propTypes = {
    subheading: string,
    heading: string,
    link: string,
    buttonText: string,
    itemConfig: array
}

export default OokaTradition
